import axios from "axios";

const PLATFORM_HOSTS = new Set(["rpsignage.com", "www.rpsignage.com", "rpsignage.in", "www.rpsignage.in", "localhost", "127.0.0.1"]);

function isLocalNetworkHost(host) {
  return (
    /^192\.168\.\d{1,3}\.\d{1,3}$/.test(host) ||
    /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host) ||
    /^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(host)
  );
}

export function isCustomStoreHost(hostname) {
  const host = String(hostname || "").toLowerCase();
  return host && !PLATFORM_HOSTS.has(host) && !isLocalNetworkHost(host);
}

export function publicStoreBase(resolvedHost) {
  const host = typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
  if (!resolvedHost && process.env.NODE_ENV === "development" && isLocalNetworkHost(host) && !["localhost", "127.0.0.1"].includes(host)) {
    return `${window.location.origin}/api/public`;
  }
  if (resolvedHost || isCustomStoreHost(typeof window !== "undefined" ? window.location.hostname : "")) {
    return `${window.location.origin}/api/public`;
  }
  return `${(process.env.REACT_APP_BACKEND_URL || "https://rpsignage.com").replace(/\/$/, "")}/api/public`;
}

export function shopperRefreshTokenKey(clientRef) {
  return `shopper_refresh_${clientRef}`;
}

let shopperRefreshInFlight = null;

export function createStoreApi(clientRef, resolvedHost) {
  const base = `${publicStoreBase(resolvedHost)}/store/${encodeURIComponent(clientRef)}`;
  // The generic /auth/refresh endpoint lives at the API root, not under
  // this store's own /public/store/{clientRef} prefix - same domain
  // resolution as everything else here, just one level up.
  const authRoot = publicStoreBase(resolvedHost).replace(/\/public$/, "");
  const tokenKey = shopperTokenKey(clientRef);
  const refreshTokenKey = shopperRefreshTokenKey(clientRef);
  const instance = axios.create({
    baseURL: base,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
  });
  instance.interceptors.request.use((cfg) => {
    const t = localStorage.getItem(tokenKey);
    if (t) cfg.headers.Authorization = `Bearer ${t}`;
    return cfg;
  });
  // Shoppers used to be silently hard-logged-out after the access token's
  // 12h expiry - the backend already issued a working refresh_token on
  // login, this just never read it back in. Same silent-refresh-and-retry
  // pattern as the main app's lib/api.js.
  instance.interceptors.response.use(
    (res) => res,
    async (error) => {
      const status = error?.response?.status;
      const cfg = error?.config || {};
      const rt = localStorage.getItem(refreshTokenKey);
      if (status !== 401 || cfg._retried || !rt) return Promise.reject(error);
      cfg._retried = true;
      try {
        if (!shopperRefreshInFlight) {
          shopperRefreshInFlight = axios
            .post(`${authRoot}/auth/refresh`, { refresh_token: rt }, { withCredentials: true })
            .finally(() => { shopperRefreshInFlight = null; });
        }
        const { data } = await shopperRefreshInFlight;
        if (!data?.access_token) throw new Error("refresh returned no access token");
        localStorage.setItem(tokenKey, data.access_token);
        if (data.refresh_token) localStorage.setItem(refreshTokenKey, data.refresh_token);
        cfg.headers = cfg.headers || {};
        cfg.headers.Authorization = `Bearer ${data.access_token}`;
        return instance(cfg);
      } catch {
        localStorage.removeItem(tokenKey);
        localStorage.removeItem(refreshTokenKey);
        return Promise.reject(error);
      }
    }
  );
  return instance;
}

export function storePublicUrl(clientRef, resolvedHost = null) {
  const origin = typeof window !== "undefined" ? window.location.origin.replace(/\/$/, "") : "";
  if (resolvedHost || (typeof window !== "undefined" && isCustomStoreHost(window.location.hostname))) {
    return origin || "/";
  }
  return `${origin}/store/${encodeURIComponent(clientRef)}`;
}

export function storePath(clientRef, sub = "", resolvedHost = null) {
  if (resolvedHost || isCustomStoreHost(typeof window !== "undefined" ? window.location.hostname : "")) {
    return sub ? `/${sub.replace(/^\//, "")}` : "/";
  }
  const base = `/store/${encodeURIComponent(clientRef)}`;
  return sub ? `${base}/${sub.replace(/^\//, "")}` : base;
}

export function shopperTokenKey(clientRef) {
  return `shopper_token_${clientRef}`;
}
